import React, { useState, useEffect, useRef, useMemo, useCallback, forwardRef, useContext } from 'react';
import ReactDOM from 'react-dom';
import { useController } from 'react-hook-form';
import ApiService from '../../services/ApiService';
import { X, Check, AlertTriangle } from 'lucide-react';
import { FeedbackContext } from '../../contexts/FeedbackContext';
import {
  DropdownArrowContainer,
  InputContainer,
  SearchInput,
  OptionsList,
  OptionGroupHeader,
  OptionItem,
  OptionItemContent,
  OptionName,
  OptionDetails,
  PriceTags,
  PriceTag,
  NoOptionsMessage,
  LoadMoreButton,
  ClearButton,
  InputWrapper,
  InputDiv,
  Label,
  Input,
  InputCell,
  SearchOptions,
  Option,
  SelectedItemsContainer,
  SelectedItem,
  SelectedItemText,
  RemoveIcon,
  ModalOverlay,
  ModalContent,
  ModalIcon,
  ModalTitle,
  ModalText,
  ErrorMessage,
  ModalButtons,
  CancelButton,
  DeleteButton,
  OptionContent,
  OptionLeft,
  CreateOptionStyle,
  PantoneSwatch,
} from './style';

interface Option {
  id: any;
  name: string;
  code?: string;
  disabled?: boolean;
  group?: string;
  groupTitle?: string;
  category?: string;
  type?: string;
  cnpj?: string;
  contato?: string;
  telefone?: string;
  contactName?: string;
  phone?: string;
  hex?: string;
  pantone?: { code: string; name?: string; hex?: string } | null;
  prices?: Array<{
    id?: number | string;
    price: string;
    unitOfMeasurement?: {
      id: number;
      name: string;
    };
  }>;
  stage?: {
    name: string;
    category: string;
    type: string;
  };
  materialId?: number;
  materialName?: string;
  isMaterialGroup?: boolean;
  [key: string]: any;
}

interface FieldConfig {
  key: string;
  label: string;
  getValue?: (option: Option) => any;
  render?: (value: any, option: Option) => React.ReactNode;
  condition?: (option: Option) => boolean;
  style?: React.CSSProperties;
}

interface EndpointConfig {
  baseUrl?: string;
  listPath: string;
  createPath?: string;
  deletePath?: string;
  searchParam?: string;
  limitParam?: string;
  offsetParam?: string;
  headers?: Record<string, string>;
  transformResponse?: (data: any) => Option[];
  transformCreatePayload?: (name: string, additionalData?: any) => any;
  transformCreateResponse?: (data: any) => Option;
  additionalParams?: Record<string, any>;
}

interface InfiniteSelectProps {
  name?: string;
  control?: any;

  value?: Option | null;
  onSelect?: (option: Option | null) => void;
  options?: Option[];

  placeholder?: string;
  searchPlaceholder?: string;
  emptyOptionsMessage?: string;
  errorOptionsMessage?: string;
  label?: string;

  fetchFunction?: (limit: number, offset: number, search?: string) => Promise<Option[]>;
  initialOptions?: Option[];

  isLoading?: boolean;
  onLoadMore?: () => void;
  hasMore?: boolean;
  searchValue?: string;
  onSearchChange?: (search: string) => void;

  disabled?: boolean;
  hasError?: boolean;
  onFocus?: () => void;
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  getOptionLabel?: (option: Option) => string;

  validateSelection?: (selectedOption: Option | null) => boolean;

  fieldsConfig?: FieldConfig[];
  showDefaultFields?: boolean;

  mode?: 'readonly' | 'crud';
  onItemCreate?: (name: string) => Promise<Option>;
  onItemDelete?: (id: any, cascade?: boolean) => Promise<void>;

  endpointConfig?: EndpointConfig;

  endpoint?: string;
  categoryId?: number;
  baseUrl?: string;

  allowCreate?: boolean;
  canCreate?: (searchValue: string) => boolean;
  createValidationMessage?: string | ((searchValue: string) => string | null);
  allowDelete?: boolean;
  allowCascadeDelete?: boolean;
  cascadeDeleteLabel?: string;
  allowFreeText?: boolean;
  minCreateLength?: number;
  showCreateOnlyIfNotExists?: boolean;
  showSelectedBelow?: boolean;
  dropdownZIndex?: number;
  dropdownForceFixed?: boolean;
  dropdownInline?: boolean;
  dropdownMinWidth?: number | string;
  showPantoneSwatch?: boolean;
  inputStyle?: React.CSSProperties;

  triggerVariant?: 'default' | 'cell';
}

interface UseInfiniteDataOptions<T> {
  fetchFunction: (limit: number, offset: number, search?: string) => Promise<T[]>;
  pageSize: number;
  initialData?: T[];
}

interface UseInfiniteDataReturn<T> {
  data: T[];
  setData: React.Dispatch<React.SetStateAction<T[]>>;
  isLoading: boolean;
  hasMore: boolean;
  loadMore: () => void;
  resetAndSearch: (search: string) => void;
  searchValue: string;
  initialize: () => void;
}

function useInfiniteData<T extends { id: any }>({
  fetchFunction,
  pageSize,
  initialData = []
}: UseInfiniteDataOptions<T>): UseInfiniteDataReturn<T> {
  const [data, setData] = useState<T[]>(initialData);
  const [isLoading, setIsLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [offset, setOffset] = useState(0);
  const [searchValue, setSearchValue] = useState('');

  const isLoadingRef = useRef(false);
  const searchTimeoutRef = useRef<NodeJS.Timeout>();
  const fetchFunctionRef = useRef(fetchFunction);
  // A última busca pedida enquanto havia requisição em voo, para não perdê-la.
  const pendingFetchRef = useRef<{ search: string; offset: number } | null>(null);
  const performFetchRef = useRef<(search: string, offset: number) => void>();
  const isInitializedRef = useRef(false);

  useEffect(() => {
    fetchFunctionRef.current = fetchFunction;
  }, [fetchFunction]);

  const performFetch = useCallback(async (search: string, currentOffset: number) => {
    if (isLoadingRef.current) {
      // Descartar aqui fazia a última tecla digitada nunca virar busca: a lista ficava
      // com o resultado de um termo anterior, sem spinner e sem nova tentativa.
      pendingFetchRef.current = { search, offset: currentOffset };
      return;
    }

    console.log('[InfiniteSelect][debug] performFetch:start', {
      search,
      currentOffset,
      pageSize,
      hasInitialData: initialData.length > 0,
    })

    isLoadingRef.current = true;
    setIsLoading(true);

    try {
      const newData = await fetchFunctionRef.current(pageSize, currentOffset, search);
      const consumedCount = Number((newData as any)?._consumedCount);
      const requestedCount = Number((newData as any)?._requestedCount);
      const safeConsumedCount = Number.isFinite(consumedCount) ? consumedCount : newData.length;
      const safeRequestedCount = Number.isFinite(requestedCount) ? requestedCount : pageSize;

      console.log('[InfiniteSelect][debug] performFetch:result', {
        search,
        currentOffset,
        returnedLength: newData.length,
        consumedCount: safeConsumedCount,
        requestedCount: safeRequestedCount,
        firstItem: newData[0] ? { id: (newData[0] as any).id, name: (newData[0] as any).name } : null,
      })

      setData(prevData => {
        const combinedData = currentOffset === 0 ? newData : [...prevData, ...newData];
        const deduplicated = Array.from(new Map(combinedData.map(item => [item.id, item])).values());
        console.log('[InfiniteSelect][debug] performFetch:dedup', {
          prevLength: prevData.length,
          combinedLength: combinedData.length,
          deduplicatedLength: deduplicated.length,
        })
        return deduplicated;
      });

      setOffset(currentOffset + safeConsumedCount);
      setHasMore(safeConsumedCount === safeRequestedCount);

      console.log('[InfiniteSelect][debug] performFetch:state-update', {
        nextOffset: currentOffset + safeConsumedCount,
        hasMore: safeConsumedCount === safeRequestedCount,
      })

    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      setHasMore(false);
    } finally {
      setIsLoading(false);
      isLoadingRef.current = false;
      const pendente = pendingFetchRef.current;
      if (pendente) {
        pendingFetchRef.current = null;
        performFetchRef.current?.(pendente.search, pendente.offset);
      }
    }
  }, [pageSize]);

  useEffect(() => {
    performFetchRef.current = performFetch;
  }, [performFetch]);

  const resetAndSearch = useCallback((search: string) => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    setSearchValue(search);

    console.log('[InfiniteSelect][debug] resetAndSearch', { search })

    searchTimeoutRef.current = setTimeout(() => {
      console.log('[InfiniteSelect][debug] resetAndSearch:debounced-fetch', { search })
      setData([]);
      setOffset(0);
      setHasMore(true);
      performFetch(search, 0);
    }, 300);
  }, [performFetch]);

  const loadMore = useCallback(() => {
    if (!hasMore || isLoading) return;
    console.log('[InfiniteSelect][debug] loadMore', {
      searchValue,
      offset,
      hasMore,
      isLoading,
    })
    performFetch(searchValue, offset);
  }, [hasMore, isLoading, performFetch, searchValue, offset]);

  const initialize = useCallback(() => {
    if (!isInitializedRef.current) {
      isInitializedRef.current = true;
      performFetch('', 0);
    }
  }, [performFetch]);

  return {
    data,
    setData,
    isLoading,
    hasMore,
    loadMore,
    resetAndSearch,
    searchValue,
    initialize
  };
}

const InfiniteSelect = forwardRef<HTMLInputElement, InfiniteSelectProps>(({
  name,
  control,
  value: controlledValue,
  onSelect: onSelectProp,
  options: controlledOptions,
  placeholder,
  searchPlaceholder = 'Buscar...',
  emptyOptionsMessage,
  errorOptionsMessage,
  label,
  fetchFunction,
  initialOptions = [],
  isLoading: controlledIsLoading,
  onLoadMore: controlledOnLoadMore,
  hasMore: controlledHasMore,
  searchValue: controlledSearchValue,
  onSearchChange: controlledOnSearchChange,
  disabled = false,
  hasError,
  onFocus: onFocusProp,
  onKeyDown,
  getOptionLabel,
  validateSelection,
  fieldsConfig = [],
  showDefaultFields = true,
  mode = 'readonly',
  onItemCreate,
  onItemDelete,
  endpointConfig,
  endpoint,
  categoryId,
  baseUrl,
  allowCreate = true,
  canCreate,
  createValidationMessage,
  allowDelete = true,
  allowCascadeDelete = false,
  cascadeDeleteLabel,
  allowFreeText = false,
  minCreateLength = 3,
  showCreateOnlyIfNotExists = false
  ,
  showSelectedBelow = false,
  dropdownZIndex = 9100,
  dropdownForceFixed = false,
  dropdownInline = false,
  dropdownMinWidth,
  showPantoneSwatch = true,
  triggerVariant = 'default',
  inputStyle,
}, ref) => {
  
  const [isOpen, setIsOpen] = useState(false);
  const [localSearchValue, setLocalSearchValue] = useState('');
  const [shouldOpenOnFocus, setShouldOpenOnFocus] = useState(true);
  const containerRef = useRef<HTMLDivElement>(null);
  const localInputRef = useRef<HTMLInputElement | null>(null);
  const dropdownOpenedAtRef = useRef<number>(0);
  const blurCloseTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [isEditing, setIsEditing] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<Option | null>(null);
  const [deleteError, setDeleteError] = useState<string>('');
  const [isCascadeDelete, setIsCascadeDelete] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [crudIsLoading, setCrudIsLoading] = useState(false);
  const { setFeedback } = useContext(FeedbackContext);
  const optionsListRef = useRef<HTMLDivElement>(null);
  const searchOptionsRef = useRef<HTMLUListElement>(null);
  const loadMoreRef = useRef<HTMLDivElement>(null);

  const controllerProps = control && name ? useController({
    name,
    control,
  }) : null;

  const fieldValue = controllerProps?.field.value || controlledValue;
  const fieldOnChange = controllerProps?.field.onChange || onSelectProp;
  const fieldRef = controllerProps?.field.ref || ref;

  const setRefs = useCallback((element: HTMLInputElement | null) => {
    if (fieldRef && typeof fieldRef === 'function') {
      fieldRef(element);
    } else if (fieldRef && 'current' in fieldRef) {
      (fieldRef as any).current = element;
    }
    localInputRef.current = element;
  }, [fieldRef]);

  const [coords, setCoords] = useState<{ left: number; top?: number; bottom?: number; width: number; maxHeight: number } | null>(null);
  const debugInstanceId = useMemo(() => `${name || 'no-name'}|${label || placeholder || 'no-label'}|${mode}`, [name, label, placeholder, mode]);

  const clearBlurCloseTimeout = useCallback(() => {
    if (blurCloseTimeoutRef.current !== null) {
      clearTimeout(blurCloseTimeoutRef.current);
      blurCloseTimeoutRef.current = null;
    }
  }, []);

  const getEndpointConfig = useCallback((): EndpointConfig | null => {
    if (endpointConfig) {
      return {
        searchParam: 'query',
        limitParam: 'limit',
        offsetParam: 'offset',
        ...endpointConfig
      };
    }

    if (endpoint) {
      let apiBaseUrl = '';

      if (baseUrl) {
        if (endpoint.startsWith('util/')) {
          apiBaseUrl = `${baseUrl}/resource`;
        } else {
          apiBaseUrl = baseUrl;
        }
      } else {
        apiBaseUrl = `${process.env.REACT_APP_GATEWAY_BASE_URL}/services/service-stage`;
      }

      return {
        baseUrl: apiBaseUrl,
        listPath: `/${endpoint}`,
        createPath: `/${endpoint}`,
        deletePath: `/${endpoint}`,
        searchParam: 'query',
        limitParam: 'limit',
        offsetParam: 'offset'
      };
    }

    return null;
  }, [endpointConfig, endpoint, baseUrl]);

  const buildUrl = useCallback((path: string, config?: EndpointConfig) => {
    const activeConfig = config || getEndpointConfig();
    if (!activeConfig) return '';

    const baseUrlToUse = activeConfig.baseUrl || '';
    if (!baseUrlToUse) return path;
    if (path.startsWith(baseUrlToUse)) return path;
    return `${baseUrlToUse}${path.startsWith('/') ? path : `/${path}`}`;
  }, [getEndpointConfig]);

  const endpointFetchFunction = useCallback(async (limit: number, offset: number, search?: string) => {
    const config = getEndpointConfig();
    if (!config || !config.listPath) return [];

    try {

      if (config.listPath === '/colors' && (!config.additionalParams || Object.keys(config.additionalParams).length === 0)) {
        return [];
      }

      const params: any = {};

      if (search) {
        params[config.searchParam || 'query'] = search;
      }

      params[config.limitParam || 'limit'] = limit;
      if (config.offsetParam === 'page') {
        params['page'] = Math.floor(offset / limit) + 1;
      } else {
        params[config.offsetParam || 'offset'] = offset;
      }

      if (categoryId !== undefined) {
        params['categoryId'] = categoryId;
      }

      if (config.additionalParams) {
        Object.assign(params, config.additionalParams);
      }

      const url = buildUrl(config.listPath, config);

      const response = await ApiService.infiniteSelect.get(url, { params });

      let data = response.data;

      if (config.transformResponse) {
        data = config.transformResponse(data);
      }

      return Array.isArray(data) ? data : [];
    } catch (error) {
      console.error('Erro ao buscar dados:', error);
      return [];
    }
  }, [getEndpointConfig, buildUrl, categoryId]);

  const activeFetchFunction = useMemo(() => {
    if (fetchFunction) return fetchFunction;
    if (mode === 'crud' && getEndpointConfig()) return endpointFetchFunction;
    return () => Promise.resolve([]);
  }, [fetchFunction, mode, getEndpointConfig, endpointFetchFunction]);

  const {
    data: infiniteData,
    setData: setInfiniteData,
    isLoading: infiniteIsLoading,
    hasMore: infiniteHasMore,
    loadMore: infiniteLoadMore,
    resetAndSearch: infiniteResetAndSearch,
    searchValue: infiniteSearchValue,
    initialize: infiniteInitialize
  } = useInfiniteData({
    fetchFunction: activeFetchFunction,
    pageSize: 10,
    initialData: initialOptions,
  });

  useEffect(() => {
    if (categoryId !== undefined) {
      infiniteResetAndSearch('');
    }
  }, [categoryId]);

  const getDisplayValue = useCallback((val: Option | null) => {
    if (!val) return '';
    if (typeof getOptionLabel === 'function') return getOptionLabel(val);

    if (typeof val !== 'object' || !val.name) return '';

    return val.code ? `${val.code} - ${val.name}` : val.name;
  }, [getOptionLabel]);

  const getSearchTermFromValue = useCallback((val: Option | null) => {
    if (!val) return '';
    if (val.code && String(val.code).trim() !== '') {
      return String(val.code).trim();
    }
    const displayVal = getDisplayValue(val);
    const compoundParts = displayVal.split(/\s+-\s+/);
    if (compoundParts[0] && compoundParts[0].trim()) {
      return compoundParts[0].trim();
    }
    return val.name || displayVal;
  }, [getDisplayValue]);

  const currentSearchValue = controlledSearchValue !== undefined ? controlledSearchValue : localSearchValue;

  const activeOptions = useMemo(() => {
    if (controlledOptions) {
      if (controlledOnSearchChange) {
        return controlledOptions;
      }
      const rawFilter = currentSearchValue.trim();
      if (!rawFilter) return controlledOptions;
      const normalizedFilter = rawFilter
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase();

      const selectedDisplayValue = getDisplayValue(fieldValue).trim();
      const isSelectedValueActive = rawFilter.toLowerCase() === selectedDisplayValue.toLowerCase();
      const selectedMatId = fieldValue?.materialId ?? (fieldValue?.id && String(fieldValue.id).startsWith('m:') ? Number(String(fieldValue.id).slice(2)) : null);
      const isMaterialGroup = Boolean(fieldValue?.isMaterialGroup || (fieldValue?.id && String(fieldValue.id).startsWith('m:')));

      const filterTokens = normalizedFilter.split(/\s+/).filter(Boolean);

      return controlledOptions.filter((opt) => {
        if (isMaterialGroup && selectedMatId != null && isSelectedValueActive) {
          if (opt.materialId != null && Number(opt.materialId) === Number(selectedMatId)) {
            return true;
          }
        }

        const name = String(opt.name || '')
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
          .toLowerCase();
        const code = String(opt.code || '')
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
          .toLowerCase();
        const label = typeof getOptionLabel === 'function'
          ? String(getOptionLabel(opt) || '')
              .normalize('NFD')
              .replace(/[\u0300-\u036f]/g, '')
              .toLowerCase()
          : '';
        const group = String(opt.group || opt.groupTitle || '')
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
          .toLowerCase();
        const materialName = String(opt.materialName || '')
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
          .toLowerCase();

        if (isSelectedValueActive && isMaterialGroup) {
          const selectedMatName = String(fieldValue?.materialName || fieldValue?.name || '')
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .toLowerCase()
            .trim();
          if (selectedMatName && materialName && (materialName === selectedMatName || materialName.includes(selectedMatName) || selectedMatName.includes(materialName))) {
            return true;
          }
        }

        if (name.includes(normalizedFilter) || code.includes(normalizedFilter) || label.includes(normalizedFilter) || group.includes(normalizedFilter) || (materialName && (materialName.includes(normalizedFilter) || normalizedFilter.includes(materialName)))) {
          return true;
        }

        if (filterTokens.length > 1) {
          const combined = `${name} ${code} ${label} ${group} ${materialName}`;
          if (filterTokens.every((token) => combined.includes(token))) {
            return true;
          }
        }

        return false;
      });
    }
    return infiniteData;
  }, [controlledOptions, controlledOnSearchChange, currentSearchValue, infiniteData, getOptionLabel, fieldValue, getDisplayValue]);
  const isCurrentlyLoading = controlledIsLoading !== undefined ? controlledIsLoading : infiniteIsLoading;
  const currentlyHasMore = controlledHasMore !== undefined ? controlledHasMore : infiniteHasMore;
  const handleLoadMore = controlledOnLoadMore || infiniteLoadMore;
  const currentSearchTerm = controlledSearchValue !== undefined ? controlledSearchValue : infiniteSearchValue;

  const searchChangeHandler = controlledOnSearchChange || infiniteResetAndSearch;

  useEffect(() => {
    const root = optionsListRef.current || searchOptionsRef.current;
    if (!root || !loadMoreRef.current) return;

    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && currentlyHasMore && !isCurrentlyLoading) {
        handleLoadMore();
      }
    }, {
      root,
      threshold: 0.1
    });

    observer.observe(loadMoreRef.current);

    return () => observer.disconnect();
  }, [currentlyHasMore, isCurrentlyLoading, handleLoadMore, isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setLocalSearchValue(getDisplayValue(fieldValue));
    }
  }, [fieldValue, getDisplayValue, isOpen]);

  useEffect(() => {
    return () => {
      clearBlurCloseTimeout();
    };
  }, [clearBlurCloseTimeout]);

  const handleClickOutside = useCallback((event: MouseEvent) => {
    if (!isOpen) return;
    const elapsedSinceOpen = Date.now() - dropdownOpenedAtRef.current;
    const recentlyOpened = elapsedSinceOpen < 180;
    if (recentlyOpened) {
      return;
    }
    const target = event.target as Node;
    const eventPath = typeof event.composedPath === 'function' ? event.composedPath() : [];
    const isTargetInPath = (element: Node | null | undefined) => {
      if (!element) return false;
      return eventPath.includes(element);
    };
    const isInsideContainer =
      (containerRef.current && containerRef.current.contains(target)) ||
      (localInputRef.current && localInputRef.current.contains(target)) ||
      isTargetInPath(containerRef.current) ||
      isTargetInPath(localInputRef.current);
    const isInsidePortal =
      (optionsListRef.current && optionsListRef.current.contains(target)) ||
      isTargetInPath(optionsListRef.current);
    const isInsideSearchPortal =
      (searchOptionsRef.current && searchOptionsRef.current.contains(target)) ||
      isTargetInPath(searchOptionsRef.current);
    if (!isInsideContainer && !isInsidePortal && !isInsideSearchPortal) {
      if (allowFreeText) {
        const trimmed = localSearchValue.trim();
        const customOption: Option | null = trimmed ? { id: trimmed, name: trimmed } : null;
        fieldOnChange?.(customOption);
      }
      setIsOpen(false);
    }
  }, [isOpen, debugInstanceId, allowFreeText, localSearchValue, fieldOnChange]);

  useEffect(() => {
    if (!isOpen) return;
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [handleClickOutside, isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setCoords(null);
      return;
    }
    dropdownOpenedAtRef.current = Date.now();

    const updateCoords = () => {
      const el = localInputRef.current || containerRef.current?.querySelector('input');
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const MARGIN = 8;
      const MAX_HEIGHT = 300;
      const parsedMinWidth =
        dropdownMinWidth != null
          ? typeof dropdownMinWidth === 'number'
            ? dropdownMinWidth
            : parseInt(String(dropdownMinWidth), 10) || 540
          : triggerVariant === 'cell'
            ? 540
            : 0;
      const resolvedWidth = Math.max(rect.width, parsedMinWidth);
      let left = rect.left;
      if (left + resolvedWidth > window.innerWidth - MARGIN) {
        left = Math.max(MARGIN, window.innerWidth - resolvedWidth - MARGIN);
      }

      const spaceBelow = window.innerHeight - rect.bottom - MARGIN;
      const spaceAbove = rect.top - MARGIN;
      if (spaceBelow >= 200 || spaceBelow >= spaceAbove) {
        const maxHeight = Math.min(MAX_HEIGHT, spaceBelow);
        setCoords({ left, top: rect.bottom + MARGIN, width: resolvedWidth, maxHeight });
      } else {
        const maxHeight = Math.min(MAX_HEIGHT, spaceAbove);
        setCoords({ left, bottom: window.innerHeight - rect.top + MARGIN, width: resolvedWidth, maxHeight });
      }
    };

    updateCoords();
    window.addEventListener('resize', updateCoords);
    window.addEventListener('scroll', updateCoords, true);
    return () => {
      window.removeEventListener('resize', updateCoords);
      window.removeEventListener('scroll', updateCoords, true);
    };
  }, [isOpen, dropdownMinWidth, triggerVariant]);

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    if (disabled) return;
    clearBlurCloseTimeout();

    console.log('[InfiniteSelect][debug] focus', {
      debugInstanceId,
      shouldOpenOnFocus,
      hasFieldValue: !!fieldValue,
      currentSearchValue,
    });

    if (!shouldOpenOnFocus) {
      setShouldOpenOnFocus(true);
    }

    setIsOpen(true);

    if (mode === 'crud') {
      setIsEditing(true);
    }

    if (onFocusProp) {
      onFocusProp();
    }

    if (infiniteInitialize) {
      infiniteInitialize();
    }

    if (fieldValue && shouldOpenOnFocus) {
      const displayVal = getDisplayValue(fieldValue);
      setLocalSearchValue(displayVal);
      const term = getSearchTermFromValue(fieldValue);
      if (searchChangeHandler) {
        searchChangeHandler(term);
      }
      setTimeout(() => {
        if (e.target) {
          const length = e.target.value.length;
          e.target.setSelectionRange(0, length);
        }
      }, 50);
    } else if (!fieldValue) {
      setLocalSearchValue('');
      if (searchChangeHandler) {
        searchChangeHandler('');
      } else if (infiniteInitialize) {
        infiniteInitialize();
      }
    }
  };

  const handleMouseDownInput = useCallback(() => {
    if (disabled) return;
    clearBlurCloseTimeout();
    if (!isOpen) {
      setIsOpen(true);
      if (fieldValue) {
        const displayVal = getDisplayValue(fieldValue);
        setLocalSearchValue(displayVal);
        const term = getSearchTermFromValue(fieldValue);
        if (searchChangeHandler) {
          searchChangeHandler(term);
        }
      } else if (infiniteInitialize) {
        infiniteInitialize();
      }
    }
  }, [clearBlurCloseTimeout, disabled, infiniteInitialize, isOpen, fieldValue, getDisplayValue, getSearchTermFromValue, searchChangeHandler]);

  const handleBlur = useCallback((e: React.FocusEvent<HTMLInputElement>) => {
    if (allowFreeText) {
      const trimmed = localSearchValue.trim();
      const isValid = canCreate ? canCreate(trimmed) : true;
      const customOption: Option | null = (trimmed && isValid) ? { id: trimmed, name: trimmed } : null;
      fieldOnChange?.(customOption);
    }
    clearBlurCloseTimeout();
    blurCloseTimeoutRef.current = setTimeout(() => {
      setIsOpen(false);
      if (mode === 'crud') {
        setIsEditing(false);
      }
      blurCloseTimeoutRef.current = null;
    }, 300);
  }, [clearBlurCloseTimeout, debugInstanceId, mode, allowFreeText, localSearchValue, fieldOnChange, canCreate]);

  const handleSelect = useCallback((option: Option) => {
    console.log('[InfiniteSelect] handleSelect chamado → option:', option);
    if (option.disabled) {
      console.log('[InfiniteSelect] handleSelect → opção desabilitada, ignorando');
      return;
    }

    if (validateSelection) {
      const valid = validateSelection(option);
      console.log('[InfiniteSelect] handleSelect → validateSelection:', valid);
      if (valid) {
        if (fieldOnChange) {
          console.log('[InfiniteSelect] handleSelect → chamando fieldOnChange (com validateSelection)');
          fieldOnChange(option);
        }
      }
    } else {
      if (fieldOnChange) {
        console.log('[InfiniteSelect] handleSelect → chamando fieldOnChange sem validação');
        fieldOnChange(option);
      } else {
        console.warn('[InfiniteSelect] handleSelect → fieldOnChange é nulo/undefined!');
      }
    }

    clearBlurCloseTimeout();
    setLocalSearchValue(getDisplayValue(option));
    setIsOpen(false);
    setShouldOpenOnFocus(true);
  }, [clearBlurCloseTimeout, fieldOnChange, validateSelection, getDisplayValue, fieldRef]);

  const handleClear = (e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    if (fieldOnChange) {
      fieldOnChange(null);
    }
    setLocalSearchValue('');
    infiniteResetAndSearch('');
  };

  const handleSearchChange = (val: string) => {
    if (!isOpen) {
      setIsOpen(true);
    }
    setLocalSearchValue(val);

    if (allowFreeText) {
      const trimmed = val.trim();
      const isValid = canCreate ? canCreate(trimmed) : true;
      const customOption: Option | null = (trimmed && isValid) ? { id: trimmed, name: trimmed } : null;
      fieldOnChange?.(customOption);
    }

    if (mode === 'crud') {
      setIsEditing(true);
      setSelectedIndex(0);
    }

    if (searchChangeHandler) {
      searchChangeHandler(val);
    }
  };

  const handleCreateItem = useCallback(async () => {
    if (!localSearchValue.trim() || crudIsLoading || localSearchValue.length < minCreateLength) {
      return;
    }

    if (canCreate && !canCreate(localSearchValue.trim())) {
      return;
    }

    setCrudIsLoading(true);
    try {
      let newItem: Option;

      if (onItemCreate) {
        newItem = await onItemCreate(localSearchValue);
      } else {
        const config = getEndpointConfig();
        if (!config) {
          throw new Error('Nenhuma função de criação ou configuração de endpoint fornecida');
        }

        let payload: any;
        if (config.transformCreatePayload) {
          const additionalData = categoryId ? { categoryId } : {};
          payload = config.transformCreatePayload(localSearchValue, additionalData);
        } else {
          payload = { name: localSearchValue };
          if (categoryId) payload.categoryId = categoryId;
          if (config.additionalParams) {
            payload = { ...payload, ...config.additionalParams };
          }
        }

        const createPath = config.createPath || config.listPath;
        const url = buildUrl(createPath, config);
        const requestConfig: any = {};

        if (config.headers) {
          requestConfig.headers = config.headers;
        }

        const response = await ApiService.infiniteSelect.post(url, payload, requestConfig);

        if (config.transformCreateResponse) {
          newItem = config.transformCreateResponse(response.data);
        } else {
          newItem = response.data;
        }
      }

      setLocalSearchValue(getDisplayValue(newItem));
      setIsOpen(false);
      setIsEditing(false);

      if (fieldOnChange) {
        fieldOnChange(newItem);
      }

      if (!controlledOptions) {
        infiniteResetAndSearch('');
      }
    } catch (error: any) {
      console.error('Erro ao criar item:', error);
      let errorMsg = 'Erro ao criar item';
      if (error?.response?.data?.message) {
        const msg = error.response.data.message;
        errorMsg = Array.isArray(msg) ? msg[0] : msg;
      } else if (error?.response?.data?.error) {
        errorMsg = error.response.data.error;
      } else if (error?.message) {
        errorMsg = error.message;
      }

      setFeedback({
        isOpen: true,
        message: errorMsg,
        statusOk: false,
      });

      setIsOpen(true);
      setIsEditing(true);
    } finally {
      setCrudIsLoading(false);
    }
  }, [localSearchValue, crudIsLoading, minCreateLength, onItemCreate, getEndpointConfig, categoryId, buildUrl, getDisplayValue, fieldOnChange, controlledOptions, infiniteResetAndSearch, setFeedback, canCreate]);

  const handleRemoveItem = useCallback(async (itemId: any, cascade?: boolean) => {
    try {
      if (onItemDelete) {
        await onItemDelete(itemId, cascade);
      } else {
        const config = getEndpointConfig();
        if (!config) {
          throw new Error('Nenhuma função de exclusão ou configuração de endpoint fornecida');
        }

        const deletePath = config.deletePath || config.listPath;
        const queryParams = new URLSearchParams();
        if (cascade) queryParams.set('cascade', 'true');
        if (config.additionalParams) {
          Object.entries(config.additionalParams).forEach(([k, v]) => {
            if (v !== undefined && v !== null) {
              queryParams.set(k, String(v));
            }
          });
        }
        const queryString = queryParams.toString();
        const pathWithId = `${deletePath.replace(/\/+$/, '')}/${itemId}`;
        const url = `${buildUrl(pathWithId, config)}${queryString ? `?${queryString}` : ''}`;
        const requestConfig: any = {};

        if (config.headers) {
          requestConfig.headers = config.headers;
        }

        await ApiService.infiniteSelect.delete(url, requestConfig);
      }

      if (!controlledOptions) {
        setInfiniteData(prev => prev.filter((item: any) => item.id !== itemId));
      }

      if (fieldValue && fieldValue.id === itemId) {
        fieldOnChange?.(null);
      }
      setLocalSearchValue('');

      if (!controlledOptions) {
        infiniteResetAndSearch('');
      }
    } catch (error: any) {
      let errorMessage = 'Erro ao excluir item';

      if (error?.response?.data?.message) {
        const msg = error.response.data.message;
        errorMessage = Array.isArray(msg) ? msg[0] : msg;
      } else if (error?.response?.status === 400) {
        errorMessage = 'Não é possível excluir este item pois está sendo usado.';
      } else if (error?.response?.status === 404) {
        errorMessage = 'Item não encontrado.';
      } else if (error?.response?.status === 500) {
        errorMessage = 'Erro interno do servidor. Tente novamente.';
      } else if (error?.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (typeof error?.response?.data === 'string') {
        errorMessage = error.response.data;
      } else if (error?.message) {
        errorMessage = error.message;
      }

      const stringError = String(errorMessage || '');
      if (
        stringError.includes('foreign key constraint') ||
        stringError.includes('violates RESTRICT')
      ) {
        errorMessage = 'Não é possível excluir este item porque ele possui registros vinculados no sistema.';
      }

      setDeleteError(errorMessage);
      throw error;
    }
  }, [onItemDelete, getEndpointConfig, buildUrl, fieldValue, fieldOnChange, controlledOptions, infiniteResetAndSearch]);

  const handleRemoveFromDropdown = useCallback(async (e: React.MouseEvent, item: Option) => {
    e.stopPropagation();
    setItemToDelete(item);
    setShowConfirmModal(true);
    setDeleteError('');
    setIsCascadeDelete(false);
    setIsDeleting(false);
  }, []);

  const confirmDelete = useCallback(async (overrideCascade?: boolean) => {
    if (!itemToDelete || isDeleting) return;

    const useCascade = overrideCascade !== undefined ? overrideCascade : isCascadeDelete;

    try {
      setIsDeleting(true);
      await handleRemoveItem(itemToDelete.id, useCascade);
      setShowConfirmModal(false);
      setItemToDelete(null);
      setDeleteError('');
      setIsCascadeDelete(false);
      setFeedback({
        isOpen: true,
        message: `O campo "${itemToDelete.name}" foi excluído com sucesso.`,
        statusOk: true,
      });
    } catch (error) {
      console.error('Erro ao confirmar exclusão:', error);
    } finally {
      setIsDeleting(false);
    }
  }, [itemToDelete, isDeleting, isCascadeDelete, handleRemoveItem, setFeedback]);

  const cancelDelete = useCallback(() => {
    setShowConfirmModal(false);
    setItemToDelete(null);
    setDeleteError('');
    setIsCascadeDelete(false);
    setIsDeleting(false);
  }, []);

  const isItemSelected = useCallback((item: Option) => {
    return fieldValue && fieldValue.id === item.id;
  }, [fieldValue]);

  const handleSetOptionByKey = useCallback((e: React.KeyboardEvent) => {
    if (mode !== 'crud') {
      if (onKeyDown) {
        onKeyDown(e as React.KeyboardEvent<HTMLInputElement>);
      }
      return;
    }

    if (e.key === 'ArrowDown' && selectedIndex < activeOptions.length) {
      e.preventDefault();
      setSelectedIndex(prev => prev + 1);
    }
    if (e.key === 'ArrowUp' && selectedIndex > 0) {
      e.preventDefault();
      setSelectedIndex(prev => prev - 1);
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      if (selectedIndex < activeOptions.length) {
        handleSelect(activeOptions[selectedIndex]);
      } else if (localSearchValue.trim()) {
        const trimmed = localSearchValue.trim();
        const exactMatch = activeOptions.find(
          item =>
            item?.name &&
            item.name.trim().toLowerCase() === trimmed.toLowerCase()
        );

        if (exactMatch) {
          handleSelect(exactMatch);
        } else if (allowFreeText) {
          const isValid = canCreate ? canCreate(trimmed) : true;
          if (isValid) {
            const customOption: Option = { id: trimmed, name: trimmed };
            setIsOpen(false);
            setIsEditing(false);
            fieldOnChange?.(customOption);
          }
        } else if (allowCreate && isEditing && isOpen && trimmed.length >= minCreateLength) {
          if (!canCreate || canCreate(trimmed)) {
            handleCreateItem();
          }
        }
      }
    }
    if (e.key === 'Tab') {
      if (allowFreeText) {
        const trimmed = localSearchValue.trim();
        const isValid = canCreate ? canCreate(trimmed) : true;
        const customOption: Option | null = (trimmed && isValid) ? { id: trimmed, name: trimmed } : null;
        fieldOnChange?.(customOption);
      }
      setIsOpen(false);
      setIsEditing(false);
    }
  }, [mode, selectedIndex, activeOptions.length, localSearchValue, isEditing, isOpen, minCreateLength, allowCreate, handleSelect, handleCreateItem, onKeyDown, allowFreeText, fieldOnChange, canCreate]);

  const getDisplayLabel = (option: Option) => {
    if (typeof getOptionLabel === 'function') {
      return getOptionLabel(option);
    }
    return option.name;
  };

  const getOptionSwatchHex = useCallback((option: Option): string | undefined => {
    const normalizeHexCandidate = (candidate: any): string | undefined => {
      if (typeof candidate !== 'string') return undefined
      const trimmed = candidate.trim()
      if (/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{4}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/.test(trimmed)) return trimmed
      return undefined
    }

    const visited = new Set<any>()

    const findHexRecursively = (input: any, depth: number): string | undefined => {
      if (depth > 6) return undefined

      const directHex = normalizeHexCandidate(input)
      if (directHex) return directHex

      if (!input || typeof input !== 'object') return undefined
      if (visited.has(input)) return undefined
      visited.add(input)

      if (Array.isArray(input)) {
        for (const item of input) {
          const nestedHex = findHexRecursively(item, depth + 1)
          if (nestedHex) return nestedHex
        }
        return undefined
      }

      const explicitHexKeys = ['_swatchHex', 'colorHex', 'hex', 'Hex']
      for (const key of explicitHexKeys) {
        if (key in input) {
          const val = (input as any)[key]
          if (typeof val === 'string') {
            const trimmed = val.trim()
            if (/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{4}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/.test(trimmed)) return trimmed
            if (/^([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(trimmed)) return `#${trimmed}`
          }
        }
      }

      const objectKeys = ['Pantone', 'pantone', 'ItemColor', 'itemColor', 'CollectionColor', 'collectionColor']
      for (const key of objectKeys) {
        if (key in input) {
          const nestedHex = findHexRecursively((input as any)[key], depth + 1)
          if (nestedHex) return nestedHex
        }
      }

      return undefined
    }

    return findHexRecursively(option, 0)
  }, [])

  const isPantoneSwatchAllowedForOption = useCallback((option: Option): boolean => {
    if (!showPantoneSwatch) return false

    const integrationModuleId = String((option as any)?._integrationModuleId || '').trim().toLowerCase()
    const integrationFieldKey = String((option as any)?._integrationFieldKey || '').trim().toLowerCase()

    if (!integrationModuleId && !integrationFieldKey) {
      return true
    }

    const isColorField = integrationFieldKey === 'color' || integrationFieldKey === 'colors'
    if (!isColorField) return false

    return integrationModuleId === 'resources' || integrationModuleId === 'fashion-stock'
  }, [showPantoneSwatch])

  const selectedSwatchHex = useMemo(() => {
    if (!fieldValue) return undefined
    if (!isPantoneSwatchAllowedForOption(fieldValue)) return undefined
    const directHex = getOptionSwatchHex(fieldValue)
    if (directHex) return directHex
    const matchedOption = activeOptions.find((opt) => String(opt.id) === String(fieldValue.id))
    if (matchedOption && isPantoneSwatchAllowedForOption(matchedOption)) {
      return getOptionSwatchHex(matchedOption)
    }
    return undefined
  }, [fieldValue, isPantoneSwatchAllowedForOption, getOptionSwatchHex, activeOptions])

  const handleOptionsScroll = useCallback((event: React.UIEvent<HTMLElement>) => {
    if (!currentlyHasMore || isCurrentlyLoading) return
    const target = event.currentTarget
    const nearBottom = target.scrollTop + target.clientHeight >= target.scrollHeight - 24
    if (nearBottom) {
      handleLoadMore()
    }
  }, [currentlyHasMore, isCurrentlyLoading, handleLoadMore])

  const getDefaultFields = (): FieldConfig[] => [
    {
      key: 'id',
      label: 'ID',
      getValue: (option) => option.id,
      render: (value) => typeof value === 'object' ? value?.id || value?.name || 'N/A' : value,
      condition: (option) => !option.description && !!option.id
    },
    {
      key: 'description',
      label: 'Descrição',
      getValue: (option) => option.description,
      render: (value) => value,
      condition: (option) => !!option.description
    }
  ];

  const renderField = (fieldConfig: FieldConfig, option: Option) => {
    if (fieldConfig.condition && !fieldConfig.condition(option)) {
      return null;
    }

    const value = fieldConfig.getValue ? fieldConfig.getValue(option) : option[fieldConfig.key];

    if (value === undefined || value === null || value === '') {
      return null;
    }

    const displayValue = fieldConfig.render ? fieldConfig.render(value, option) : value;

    return (
      <span key={fieldConfig.key} style={fieldConfig.style}>
        {fieldConfig.label ? <><strong>{fieldConfig.label}:</strong>{' '}</> : null}{displayValue}
      </span>
    );
  };

  const shouldShowDetails = (option: Option) => {
    const allFields = showDefaultFields ? [...getDefaultFields(), ...fieldsConfig] : fieldsConfig;
    return allFields.some(field => {
      if (field.condition && !field.condition(option)) {
        return false;
      }
      const value = field.getValue ? field.getValue(option) : option[field.key];
      return value !== undefined && value !== null && value !== '';
    });
  };

  const searchOptionsStyle = (() => {
    if (dropdownInline) {
      return {
        position: 'absolute',
        left: 0,
        top: 'calc(100% + 8px)',
        width: 'max-content',
        minWidth: coords?.width ? `${coords.width}px` : '100%',
        maxWidth: '90vw',
        zIndex: dropdownZIndex,
      };
    }
    if (dropdownForceFixed) {
      return {
        position: 'fixed',
        left: coords?.left || 0,
        ...(coords?.bottom !== undefined ? { bottom: coords.bottom, top: 'unset' } : { top: coords?.top || 0 }),
        width: 'max-content',
        minWidth: coords?.width ? `${coords.width}px` : undefined,
        maxWidth: '90vw',
        maxHeight: coords?.maxHeight,
        overflowY: 'auto',
        overflowX: 'hidden',
        zIndex: dropdownZIndex,
      };
    }
    if (coords) {
      return {
        position: 'fixed',
        left: coords.left,
        ...(coords.bottom !== undefined ? { bottom: coords.bottom, top: 'unset' } : { top: coords.top }),
        width: 'max-content',
        minWidth: `${coords.width}px`,
        maxWidth: '90vw',
        maxHeight: coords.maxHeight,
        overflowY: 'auto',
        overflowX: 'hidden',
        zIndex: dropdownZIndex,
      };
    }
    return {};
  })();

  const renderedOptionItems = useMemo(() => {
    let lastGroup: string | null = null;
    const items: React.ReactNode[] = [];

    activeOptions.forEach((option, index) => {
      const allFields = showDefaultFields ? [...getDefaultFields(), ...fieldsConfig] : fieldsConfig;
      const optSwatchHex = isPantoneSwatchAllowedForOption(option) ? getOptionSwatchHex(option) : undefined;
      const groupName = option.group || option.groupTitle;

      if (groupName && groupName !== lastGroup) {
        lastGroup = groupName;
        items.push(
          <OptionGroupHeader key={`group-header-${groupName}-${index}`}>
            <span>{groupName}</span>
          </OptionGroupHeader>
        );
      }

      items.push(
        <OptionItem
          key={option.id ?? index}
          onClick={() => handleSelect(option)}
          disabled={option.disabled}
          isHighlighted={fieldValue && fieldValue.id === option.id}
        >
          <OptionItemContent>
            <OptionName style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              {optSwatchHex !== undefined && (
                <PantoneSwatch hex={optSwatchHex} title={optSwatchHex} />
              )}
              {getDisplayLabel(option)}
            </OptionName>
            {shouldShowDetails(option) && (
              <OptionDetails>
                {allFields.map(field => renderField(field, option))}
              </OptionDetails>
            )}
            {option.hasPriceInUnit === false && (
              <PriceTags>
                <PriceTag style={{ background: '#FEF3C7', color: '#92660F' }}>
                  ⚠ sem preço na unidade do item
                </PriceTag>
              </PriceTags>
            )}
            {option.prices && option.prices.length > 0 && (
              <PriceTags>
                {option.prices.map((price: any, pIdx: number) => (
                  <PriceTag key={pIdx}>
                    R$ {parseFloat(price.price).toFixed(2)}/{price.unit || price.unitOfMeasurement?.name || price.UnitOfMeasurement?.name || 'unidade'}
                  </PriceTag>
                ))}
              </PriceTags>
            )}
          </OptionItemContent>
        </OptionItem>
      );
    });

    return items;
  }, [activeOptions, fieldValue, handleSelect, fieldsConfig, showDefaultFields, getOptionSwatchHex, isPantoneSwatchAllowedForOption]);

  if (mode === 'crud') {
    const TriggerInput = triggerVariant === 'cell' ? InputCell : Input;
    return (
      <>
      <InputDiv ref={containerRef}>
        {label && <Label>{label}</Label>}
        <InputWrapper>
          {selectedSwatchHex !== undefined && (
            <span style={{ position: 'absolute', left: triggerVariant === 'cell' ? '8px' : '10px', top: '50%', transform: 'translateY(-50%)', zIndex: 1, pointerEvents: 'none', display: 'flex', alignItems: 'center' }}>
              <PantoneSwatch hex={selectedSwatchHex} />
            </span>
          )}
          <TriggerInput
            ref={setRefs}
            type="text"
            placeholder={placeholder}
            value={currentSearchValue}
            title={disabled ? "Campo desabilitado" : (currentSearchValue || undefined)}
            onMouseDown={handleMouseDownInput}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onChange={(e) => handleSearchChange(e.target.value)}
            onKeyDown={(e) => handleSetOptionByKey(e)}
            disabled={crudIsLoading || disabled}
            name={name}
            style={{
              ...inputStyle,
              paddingLeft: selectedSwatchHex ? (triggerVariant === 'cell' ? '28px' : '32px') : undefined,
              paddingRight: fieldValue ? '32px' : undefined,
            }}
            className="inline-input"
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck="false"
            data-lpignore="true"
            data-form-type="other"
          />
          {fieldValue && !disabled && (
            <ClearButton
              onClick={(e) => {
                e.stopPropagation();
                handleClear();
              }}
              title="Limpar seleção"
              type="button"
              style={{ right: triggerVariant === 'cell' ? '10px' : '14px' }}
            >
              <X size={14} color="#696969" />
            </ClearButton>
          )}
        </InputWrapper>
        {isOpen && coords && !disabled &&
          ReactDOM.createPortal(
          <SearchOptions ref={searchOptionsRef} style={searchOptionsStyle as React.CSSProperties} onScroll={handleOptionsScroll} onMouseDown={(e) => e.preventDefault()}>
            {isCurrentlyLoading && activeOptions.length === 0 && <Option style={{ cursor: 'default', backgroundColor: '#f8f9fa', color: '#6c757d' }}>Carregando...</Option>}
            {!isCurrentlyLoading && activeOptions.length === 0 && <Option style={{ cursor: 'default', backgroundColor: '#f8f9fa', color: '#6c757d' }}>{currentSearchValue ? (errorOptionsMessage || 'Nenhum resultado encontrado') : (emptyOptionsMessage || 'Nenhum item disponível')}</Option>}
            {(() => {
              let lastGroup: string | null = null;
              return activeOptions.map((item, index) => {
                const swatchHex = isPantoneSwatchAllowedForOption(item) ? getOptionSwatchHex(item) : undefined;
                const groupName = item.group || item.groupTitle;
                const showGroupHeader = Boolean(groupName && groupName !== lastGroup);
                if (showGroupHeader) {
                  lastGroup = groupName;
                }

                return (
                  <React.Fragment key={item.id ?? index}>
                    {showGroupHeader && (
                      <OptionGroupHeader key={`crud-group-${groupName}-${index}`}>
                        <span>{groupName}</span>
                      </OptionGroupHeader>
                    )}
                    <Option
                      onClick={() => handleSelect(item)}
                      isSelected={index === selectedIndex}
                    >
                      <OptionContent>
                        <OptionLeft>
                          {isItemSelected(item) && <Check size={14} color="#28a745" />}
                          {swatchHex !== undefined && (
                            <PantoneSwatch hex={swatchHex} title={swatchHex} />
                          )}
                          <span>{getDisplayLabel(item)}</span>
                        </OptionLeft>
                        {allowDelete && (
                          <X
                            size={14}
                            style={{ cursor: 'pointer', color: '#dc3545' }}
                            onClick={(e) => handleRemoveFromDropdown(e, item)}
                          />
                        )}
                      </OptionContent>
                    </Option>
                  </React.Fragment>
                );
              });
            })()}
            {allowCreate && currentSearchValue.trim() && currentSearchValue.trim().length >= minCreateLength && (() => {
              const trimmedSearch = currentSearchValue.trim().toLowerCase();
              const exactMatch = activeOptions.find(item =>
                item?.name && item.name.trim().toLowerCase() === trimmedSearch
              );

              if (exactMatch) {
                return null;
              }

              if (canCreate && !canCreate(currentSearchValue.trim())) {
                const message = typeof createValidationMessage === 'function'
                  ? createValidationMessage(currentSearchValue.trim())
                  : createValidationMessage;

                if (message) {
                  return (
                    <Option key="create-validation-msg" style={{ cursor: 'default', backgroundColor: '#FEF2F2', color: '#DC2626', fontSize: '0.82rem', padding: '0.5rem 0.75rem' }}>
                      {message}
                    </Option>
                  );
                }
                return null;
              }

              return (
                <Option
                  onClick={handleCreateItem}
                  isSelected={selectedIndex === activeOptions.length}
                >
                  <CreateOptionStyle>
                    + Criar &ldquo;{currentSearchValue.trim()}&rdquo;
                  </CreateOptionStyle>
                </Option>
              );
            })()}
            {currentlyHasMore && (
              <div ref={loadMoreRef} style={{ height: '1px', visibility: 'hidden' }} />
            )}
            {isCurrentlyLoading && <Option style={{ textAlign: 'center', color: '#666', fontStyle: 'italic' }}>Carregando mais...</Option>}
          </SearchOptions>,
          document.body
          )
        }

        {fieldValue && showSelectedBelow === true && (
          <SelectedItemsContainer>
            <SelectedItem>
              <SelectedItemText>{getDisplayValue(fieldValue)}</SelectedItemText>
              <RemoveIcon disabled={disabled}>
                <X
                  size={16}
                  onClick={disabled ? undefined : handleClear}
                />
              </RemoveIcon>
            </SelectedItem>
          </SelectedItemsContainer>
        )}

        {showConfirmModal &&
          ReactDOM.createPortal(
            <ModalOverlay>
              <ModalContent>
                <ModalIcon>
                  <AlertTriangle size={48} />
                </ModalIcon>
                <ModalTitle>
                  Confirmar Exclusão
                </ModalTitle>
                <ModalText>
                  Tem certeza que deseja excluir &ldquo;{itemToDelete?.name}&rdquo;?
                </ModalText>

                {allowCascadeDelete && (
                  <label
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      margin: '12px 0 6px 0',
                      fontSize: '13px',
                      color: '#334155',
                      cursor: 'pointer',
                      userSelect: 'none',
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={isCascadeDelete}
                      onChange={(e) => setIsCascadeDelete(e.target.checked)}
                      style={{ cursor: 'pointer', width: '16px', height: '16px', accentColor: '#dc3545' }}
                    />
                    <span>{cascadeDeleteLabel || 'Excluir registros vinculados em cascata'}</span>
                  </label>
                )}

                {deleteError && (
                  <ErrorMessage>
                    {deleteError}
                  </ErrorMessage>
                )}

                {deleteError && allowCascadeDelete && !isCascadeDelete && (
                  <button
                    type="button"
                    onClick={() => {
                      setIsCascadeDelete(true);
                      confirmDelete(true);
                    }}
                    style={{
                      marginTop: '8px',
                      padding: '8px 12px',
                      backgroundColor: '#fff1f2',
                      border: '1px solid #fecdd3',
                      borderRadius: '6px',
                      color: '#e11d48',
                      fontSize: '12px',
                      fontWeight: 600,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '6px',
                      width: '100%',
                    }}
                  >
                    Excluir tudo em cascata agora
                  </button>
                )}

                <ModalButtons>
                  <CancelButton onClick={cancelDelete} disabled={isDeleting}>
                    Cancelar
                  </CancelButton>
                  <DeleteButton onClick={() => confirmDelete()} disabled={isDeleting}>
                    {isDeleting ? 'Excluindo...' : (isCascadeDelete ? 'Excluir em cascata' : 'Excluir')}
                  </DeleteButton>
                </ModalButtons>
              </ModalContent>
            </ModalOverlay>,
            document.body
          )
        }
      </InputDiv>
      </>
    );
  }

  const readonlyCellTrigger = triggerVariant === 'cell';

  return (
    <InputContainer ref={containerRef}>
      {label && <Label style={{ marginBottom: '0.5rem' }}>{label}</Label>}
      <InputWrapper>
        {selectedSwatchHex !== undefined && (
          <span style={{ position: 'absolute', left: readonlyCellTrigger ? '12px' : '10px', top: '50%', transform: 'translateY(-50%)', zIndex: 1, pointerEvents: 'none', display: 'flex', alignItems: 'center' }}>
            <PantoneSwatch hex={selectedSwatchHex} />
          </span>
        )}
        <SearchInput
          ref={setRefs}
          type="text"
          value={currentSearchValue}
          onChange={(e) => handleSearchChange(e.target.value)}
          onMouseDown={handleMouseDownInput}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          title={disabled ? "Campo desabilitado" : (currentSearchValue || undefined)}
          disabled={disabled}
          hasError={!!hasError}
          isOpen={isOpen}
          name={name}
          style={{
            ...inputStyle,
            paddingLeft: selectedSwatchHex ? (readonlyCellTrigger ? '36px' : '32px') : undefined,
          }}
          className="inline-input"
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck="false"
          data-lpignore="true"
          data-form-type="other"
          $cell={readonlyCellTrigger}
          $cellWithClear={readonlyCellTrigger && !!fieldValue}
        />
        {fieldValue && (
          <ClearButton onClick={handleClear} title="Limpar" type="button" $cell={readonlyCellTrigger}>
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
              <path d="M1 1L11 11M1 11L11 1" stroke="#666" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </ClearButton>
        )}
        <DropdownArrow isOpen={isOpen} $cell={readonlyCellTrigger} />
      </InputWrapper>

      {isOpen && coords && ReactDOM.createPortal(
        <OptionsList isOpen={isOpen} ref={optionsListRef} style={searchOptionsStyle as React.CSSProperties} onScroll={handleOptionsScroll} onMouseDown={(e) => e.preventDefault()}>
        {isCurrentlyLoading && activeOptions.length === 0 && <OptionItem>Carregando...</OptionItem>}
        {!isCurrentlyLoading && activeOptions.length === 0 && <NoOptionsMessage>{errorOptionsMessage || (currentSearchValue ? 'Nenhum resultado encontrado' : (emptyOptionsMessage || searchPlaceholder || 'Nenhum item'))}</NoOptionsMessage>}
        {renderedOptionItems}
        {currentlyHasMore && (
          <div ref={loadMoreRef} style={{ height: '1px', visibility: 'hidden' }} />
        )}
        {isCurrentlyLoading && <OptionItem style={{ textAlign: 'center', color: '#666', fontStyle: 'italic' }}>Carregando mais...</OptionItem>}
        </OptionsList>,
        document.body
      )}
    </InputContainer>
  );
});

const DropdownArrow: React.FC<{ isOpen: boolean; $cell?: boolean }> = ({ isOpen, $cell }) => {
  return (
    <DropdownArrowContainer isOpen={isOpen} $cell={$cell}>
      <svg width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M1 1.5L6 6.5L11 1.5" stroke="#696969" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </DropdownArrowContainer>
  );
};

InfiniteSelect.displayName = 'InfiniteSelect';

export type { Option, FieldConfig, EndpointConfig, InfiniteSelectProps };
export default InfiniteSelect;
